import { createAsyncThunk } from "@reduxjs/toolkit";
import { getFirebaseAuthToken } from "../../helpers/firebase_helper";
import { downloadBlob } from "../../utils/utils";
import axios from "axios";
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ----------------------------------------------------------------------
// Admin-wide CSV downloads (every entity across the system for one role).
// Backend: GET /dashboard/reports/admin/all/{nationals|gas|brokers|employers}
// Auth: admin role required (enforced server-side).
// ----------------------------------------------------------------------

const buildAdminReportDownloader = (path, filename) =>
  async () => {
    const token = await getFirebaseAuthToken();
    const response = await fetch(
      `${API_BASE}/dashboard/reports/admin/all/${path}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Error downloading report");
    }
    const blob = await response.blob();
    downloadBlob(blob, filename);
    return response;
  };

export const downloadAllNationalsReport = createAsyncThunk(
  "admin/downloadAllNationalsReport",
  buildAdminReportDownloader("nationals", "Nationals_report.csv")
);

export const downloadAllGAsReport = createAsyncThunk(
  "admin/downloadAllGAsReport",
  buildAdminReportDownloader("gas", "GAs_report.csv")
);

export const downloadAllBrokersReport = createAsyncThunk(
  "admin/downloadAllBrokersReport",
  buildAdminReportDownloader("brokers", "Brokers_report.csv")
);

export const downloadAllEmployersReport = createAsyncThunk(
  "admin/downloadAllEmployersReport",
  buildAdminReportDownloader("employers", "Employers_report.csv")
);

export const getRolesAdmin = createAsyncThunk(
  "admin/getRolesAdmin",
  async ({ role, status, page, query }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/${role}?status=${status}&page=${page}&query=${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching employees");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const getRoleById = createAsyncThunk(
  "admin/getRoleById",
  async ({ role, id }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(`${API_BASE}/dashboard/${role}/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Error fetching role");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const getRolesForTables = createAsyncThunk(
  "ga/getRolesForTables",
  async ({ id, userRole, role, status, page, query = "" }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/${userRole}/${id}/${role}?status=${status}&page=${page}&query=${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching employers");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

// POST
export const postEmployeeByEmployerId = createAsyncThunk(
  "employer/postEmployeeByEmployerId",
  async ({ id, data, multiple }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/employer/${id}/employees${multiple ? "/multiple" : ""
        }`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: multiple
            ? JSON.stringify({ employees: data })
            : JSON.stringify(data),
        }
      );
      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);
    }
  }
);

export const postEmployerByBrokerId = createAsyncThunk(
  "broker/postEmpoloyerByBrokerId",
  async ({ id, data }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/broker/${id}/employers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(data),
        }
      );
      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);
    }
  }
);

export const addUserByRole = createAsyncThunk(
  "admin/addUserByRole",
  async ({ data, role }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(`${API_BASE}/dashboard/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify({ ...data, role }),
      });
      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);
    }
  }
);

export const postRoleForGaId = createAsyncThunk(
  "ga/postRoleForGaId",
  async ({ id, data, role }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(`${API_BASE}/dashboard/ga/${id}/${role}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(data),
      });
      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);
    }
  }
);

export const addByNationalsId = createAsyncThunk(
  "nationals/addByNationalsId",
  async ({ id, data }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(`${API_BASE}/dashboard/nationals/${id}/ga`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Error adding GA");
      }
      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);

      throw error;
    }
  }
);

export const getAdminInvoices = createAsyncThunk(
  "admin/getAdminInvoices",
  async ({ status, page, query = "", currentMonth }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/invoices?paid=${status}&page=${page}&query=${query}&currentMonth=${currentMonth}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching invoices");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const expireInvoice = createAsyncThunk(
  "admin/expireInvoice",
  async ({ invoiceId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/invoices/${invoiceId}/invalidate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Error expiring invoice");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

// Admin Panel
export const getAnalytics = createAsyncThunk("admin/getAnalytics", async () => {
  try {
    const token = await getFirebaseAuthToken();
    const response = await fetch(
      `${API_BASE}/dashboard/admin/analytics/general`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Error fetching analytics");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
});

export const getInvoicesStatus = createAsyncThunk(
  "admin/getInvoicesStatus",
  async ({ page }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/invoices/status-updates?page=${page}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching invoices status");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const getCommissionStatus = createAsyncThunk(
  "admin/getComissionStatus",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commissions/status-updates`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching commissions status");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

// SIMULATE PANEL
export const simulateEffectiveDate = createAsyncThunk(
  "admin/simulateEffectiveDate",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/employers/effective-date`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const simulateEffectiveDateByID = createAsyncThunk(
  "admin/simulateEffectiveDateByID",
  async ({ employerId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/employers/${employerId}/effective-date`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const lockAllEmoployees = createAsyncThunk(
  "admin/lockAllEmoployees",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/employers/lock-employees`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error ");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const lockEmployerEmployeesById = createAsyncThunk(
  "admin/lockEmployerEmployeesById",
  async ({ employerId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/employers/${employerId}/lock-employees`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const terminateDueEmployees = createAsyncThunk(
  "admin/terminateDueEmployees",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/employers/terminate-due-employees`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const calculatePastMonthCommissions = createAsyncThunk(
  "admin/calculatePastMonthCommissions",
  async ({ userId, currentMonth }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/commissions/${userId}?currentMonth=${currentMonth}`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Rebuild Algolia index
export const rebuildAlgoliaIndex = createAsyncThunk(
  "admin/rebuildAlgoliaIndex",
  async ({ index }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/algolia/rebuild-index`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ index }),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      // Expecting a boolean or { success: true }
      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// PUT EMPLOYEE STATUS
export const updateEmployeeStatus = createAsyncThunk(
  "admin/updateEmployeeStatus",
  async ({ employeeId, status }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/employees/${employeeId}/status/${status}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },

          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// JOBS EXECUTIONS
export const jobsExecutions = createAsyncThunk(
  "admin/jobsExecutions",
  async ({ page }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/jobs/executions?page=${page}`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);
// Dashboard Admin
export const getAppUsersPaginated = createAsyncThunk(
  "admin/getAppUsersPaginated",
  async ({ page, query, filterParam }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users?page=${page}&query=${query}&orderby=${filterParam}`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const getAppUserById = createAsyncThunk(
  "admin/getAppUsersById",
  async (userId) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const adminLoginAsUser = createAsyncThunk(
  "admin/adminLoginAsUser",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}/login`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error("Error logging in as user");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const analyticsUsersApp = createAsyncThunk(
  "admin/analyticsUsersApp",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/analytics`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const cancelImmediatelySubscription = createAsyncThunk(
  "admin/cancelImmediatelySubscription",
  async ({ userId, immediate = false }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/cancel-immediately`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ immediate }),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const getCEbyUserId = createAsyncThunk(
  "admin/getCEbyUserId",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/${userId}/mfp-client-eligibility`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }
      if (response.headers.get("Content-Length") === "0") {
        throw new Error("No content");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const getAllCE = createAsyncThunk("admin/getAllCE", async ({ page }) => {
  try {
    const token = await getFirebaseAuthToken();
    const response = await fetch(
      `${API_BASE}/dashboard/admin/mfp-client-eligibility?page=${page}`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Error");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const updateClientEligibilityByMemberId = createAsyncThunk(
  "admin/updateClientEligibilityByMemberId",
  async ({ memberId, data }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/mfp-client-eligibility/${memberId}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(data),
        }
      );
      if (!response.ok) {
        throw new Error("Error");
      }

      const res = await response.json();
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Delete Users
export const deleteUsers = createAsyncThunk(
  "admin/deleteUsers",
  async ({ userId, role }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error deleting user");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const cancelDeleteUsers = createAsyncThunk(
  "admin/cancelDeleteUsers",
  async ({ userId, role }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/cancel-deletion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Error deleting user");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

// Login as user
export const loginAsUser = createAsyncThunk(
  "admin/loginAsUser",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

export const addCommissionManually = createAsyncThunk(
  "admin/addCommissionManually",
  async ({ dashboardUserId, amount, description }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commissions/manual`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ dashboardUserId, amount, description }),
        }
      );

      if (!response.ok) {
        throw new Error("Error adding commission");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);


// Fetch customer lifetime metrics
export const fetchCustomerLifetimeMetrics = createAsyncThunk(
  'billingAnalytics/fetchCustomerLifetimeMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/billing-analytics/customer-lifetime`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching customer lifetime metrics");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching customer lifetime metrics:', error);
      throw error;
    }
  }
);

// Fetch customer retention metrics
export const fetchCustomerRetentionMetrics = createAsyncThunk(
  'billingAnalytics/fetchCustomerRetentionMetrics',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const { period = 'monthly', fromDate = '', toDate = '' } = params;
      const response = await fetch(
        `${API_BASE}/dashboard/admin/billing-analytics/retention-metrics?period=${period}&fromDate=${fromDate}&toDate=${toDate}`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching customer retention metrics");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching customer retention metrics:', error);
      throw error;
    }
  }
);

// Fetch LTV/ARPU Stats
export const fetchLTVStats = createAsyncThunk(
  'billingAnalytics/fetchLTVStats',
  async (_, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/billing-analytics/ltv`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching LTV stats");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching LTV stats:', error);
      throw error;
    }
  }
);

// Fetch LTV/ARPU History
export const fetchLTVHistory = createAsyncThunk(
  'billingAnalytics/fetchLTVHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const { startDate, endDate } = params;
      let url = `${API_BASE}/dashboard/admin/billing-analytics/ltv/history`;

      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Error fetching LTV history");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching LTV history:', error);
      throw error;
    }
  }
);

// Fetch KPIs Dashboard
export const fetchKPIStats = createAsyncThunk(
  'billingAnalytics/fetchKPIStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const { forceRefresh } = params;
      let url = `${API_BASE}/dashboard/admin/billing-analytics/kpis`;

      if (forceRefresh) {
        url += '?forceRefresh=true';
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization: `${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Error fetching KPI stats");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching KPI stats:', error);
      throw error;
    }
  }
);

// Fetch Theme Custom Plans
export const fetchThemeCustomPlans = createAsyncThunk(
  'admin/fetchThemeCustomPlans',
  async ({ nationalId }, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/theme/national/${nationalId}/plans`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching theme custom plans");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching theme custom plans:', error);
      throw error;
    }
  }
);

// Save Theme Custom Plans
export const saveThemeCustomPlans = createAsyncThunk(
  'admin/saveThemeCustomPlans',
  async ({ nationalId, customPlans }, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/theme/national/${nationalId}/plans`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ customPlans }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Error saving theme custom plans");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving theme custom plans:', error);
      throw error;
    }
  }
);

// Delete Theme Custom Plans
export const deleteThemeCustomPlans = createAsyncThunk(
  'admin/deleteThemeCustomPlans',
  async ({ nationalId }, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/theme/national/${nationalId}/plans`,
        {
          method: "DELETE",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error deleting theme custom plans");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting theme custom plans:', error);
      throw error;
    }
  }
);

// Fetch All Services Status
export const fetchServicesStatus = createAsyncThunk(
  'admin/fetchServicesStatus',
  async (_, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/admin/status`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching services status");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching services status:', error);
      throw error;
    }
  }
);

// Refresh billing analytics cache
export const refreshBillingAnalyticsCache = createAsyncThunk(
  'billingAnalytics/refreshCache',
  async (_, { rejectWithValue }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/billing-analytics/refresh-cache`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Error refreshing billing analytics cache");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error refreshing billing analytics cache:', error);
      throw error;
    }
  }
);


// Get dashboard users paginated
export const getDashboardUsersPaginated = createAsyncThunk(
  "admin/getDashboardUsersPaginated",
  async ({ page, query = '', filterParam, sortBy, sortOrder }) => {
    try {
      const token = await getFirebaseAuthToken();


      let url = `${API_BASE}/dashboard/admin/dashboard-users?page=${page}`
      if (query) {
        url += `&query=${query}`;
      }

      if (sortBy) {
        url += `&sortBy=${sortBy}`;
      }

      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`;
      }


      const response = await fetch(
        url,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching dashboard users");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

export const fetchMemberCards = createAsyncThunk(
  "admin/fetchMemberCards",
  async ({ userId, plus, regular }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/cards?plus=${plus}&regular=${regular}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching member cards");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching member cards:', error);
      throw error;
    }
  }
);

export const sendMemberCards = createAsyncThunk(
  "admin/sendMemberCards",
  async ({ userId, plus, regular, email }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/cards/send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ plus, regular, email }),
        }
      );

      if (!response.ok) {
        throw new Error("Error sending member cards");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending member cards:', error);
      throw error;
    }
  }
);

export async function adminChangeUserPassword({ userId, newPassword }) {
  try {
    const token = await getFirebaseAuthToken();
    const response = await fetch(
      `${API_BASE}/dashboard/admin/users/${userId}/change-password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify({ newPassword }),
      }
    );

    if (!response.ok) {
      throw new Error("Error changing user password");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error changing user password:', error);
    throw error;
  }
}


export const getUserBillingHistory = createAsyncThunk(
  "admin/getUserBillingHistory",
  async ({ userId, page }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/app/billing/admin/history?userId=${userId}&page=${page}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching user billing history");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user billing history:', error);
      throw error;
    }
  }
);

// ADMIN COMMISSION STRUCTURES MANAGEMENT

// Get all commission structures
export const getAdminCommissionStructures = createAsyncThunk(
  "admin/getAdminCommissionStructures",
  async ({ userId = null }) => {
    try {
      const token = await getFirebaseAuthToken();
      const queryParam = userId ? `?userId=${userId}` : '';
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commission-structures${queryParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching commission structures");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching commission structures:', error);
      throw error;
    }
  }
);

// Create commission structure
export const createAdminCommissionStructure = createAsyncThunk(
  "admin/createAdminCommissionStructure",
  async (structureData) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commission-structures`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(structureData),
        }
      );

      if (!response.ok) {
        throw new Error("Error creating commission structure");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating commission structure:', error);
      throw error;
    }
  }
);

// Update commission structure
export const updateAdminCommissionStructure = createAsyncThunk(
  "admin/updateAdminCommissionStructure",
  async ({ structureId, structureData }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commission-structures/${structureId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(structureData),
        }
      );

      if (!response.ok) {
        throw new Error("Error updating commission structure");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating commission structure:', error);
      throw error;
    }
  }
);

// Delete commission structure
export const deleteAdminCommissionStructure = createAsyncThunk(
  "admin/deleteAdminCommissionStructure",
  async ({ structureId, nationalId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/commission-structures/${structureId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ nationalId }),
        }
      );

      if (!response.ok) {
        throw new Error("Error deleting commission structure");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting commission structure:', error);
      throw error;
    }
  }
);

// Update user custom commission amount
export const updateUserCommissionAmount = createAsyncThunk(
  "admin/updateUserCommissionAmount",
  async ({ userId, customCommissionTotalAmount }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/commission-amount`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ customCommissionTotalAmount }),
        }
      );

      if (!response.ok) {
        throw new Error("Error updating user commission amount");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user commission amount:', error);
      throw error;
    }
  }
);

// Set user commission structure
export const setUserCommissionStructure = createAsyncThunk(
  "admin/setUserCommissionStructure",
  async ({ userId, customCommissionStructureId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/commission-structure`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ customCommissionStructureId }),
        }
      );

      if (!response.ok) {
        throw new Error("Error setting user commission structure");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error setting user commission structure:', error);
      throw error;
    }
  }
);

export const getUserCustomPricing = createAsyncThunk(
  "admin/getUserCustomPricing",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/pricing/users/${userId}/custom-pricing`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching user custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user custom pricing:', error);
      throw error;
    }
  }
);

export const updateUserCustomPricing = createAsyncThunk(
  "admin/updateUserCustomPricing",
  async ({ userId, individualPlanAmount, familyPlanAmount, allowPricingOverride = true }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/pricing/users/${userId}/custom-pricing`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({
            individualPlanAmount,
            familyPlanAmount,
            allowPricingOverride,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Error updating user custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user custom pricing:', error);
      throw error;
    }
  }
);

export const removeUserCustomPricing = createAsyncThunk(
  "admin/removeUserCustomPricing",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/pricing/users/${userId}/custom-pricing`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error removing user custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing user custom pricing:', error);
      throw error;
    }
  }
);

export const getUserPricingHierarchy = createAsyncThunk(
  "admin/getUserPricingHierarchy",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/pricing/users/${userId}/pricing-hierarchy`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching user pricing hierarchy");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user pricing hierarchy:', error);
      throw error;
    }
  }
);

// ADMIN UPLINE MANAGEMENT

// Update dashboard user upline
export const updateDashboardUserUpline = createAsyncThunk(
  "admin/updateDashboardUserUpline",
  async ({ userId, nationalId, gaId, brokerId }) => {
    try {
      const token = await getFirebaseAuthToken();

      // Build request body with only defined values
      const body = {};
      if (nationalId !== undefined) body.nationalId = nationalId;
      if (gaId !== undefined) body.gaId = gaId;
      if (brokerId !== undefined) body.brokerId = brokerId;

      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/upline`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Error updating user upline");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user upline:', error);
      throw error;
    }
  }
);

// Convert GA to National
export const convertGAToNational = createAsyncThunk(
  "admin/convertGAToNational",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/users/${userId}/convert-to-national`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Error converting GA to National");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting GA to National:', error);
      throw error;
    }
  }
);

// Get all users by role (for dropdown selection)
export const getUsersByRole = createAsyncThunk(
  "admin/getUsersByRole",
  async ({ role }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/${role}?status=active&page=1&query=`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching ${role}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching ${role}:`, error);
      throw error;
    }
  }
);

// SCHEDULE MONITORING

// Get schedule monitoring report
export const getScheduleMonitoringReport = createAsyncThunk(
  "admin/getScheduleMonitoringReport",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/schedule-monitoring/breakdown`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching schedule monitoring report");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching schedule monitoring report:', error);
      throw error;
    }
  }
);

// Refresh schedule monitoring report
export const refreshScheduleMonitoringReport = createAsyncThunk(
  "admin/refreshScheduleMonitoringReport",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/schedule-monitoring/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Error refreshing schedule monitoring report");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error refreshing schedule monitoring report:', error);
      throw error;
    }
  }
);

// Grant Free Pass to user
export const grantFreePass = createAsyncThunk(
  "admin/grantFreePass",
  async ({ userId, reason }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}/grant-free-pass`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error granting free pass");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error granting free pass:', error);
      throw error;
    }
  }
);

// Reactivate Subscription
export const reactivateSubscription = createAsyncThunk(
  "admin/reactivateSubscription",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}/reactivate-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error reactivating subscription");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }
);

// Get Payment Method
export const getPaymentMethod = createAsyncThunk(
  "admin/getPaymentMethod",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}/payment-method`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching payment method");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching payment method:', error);
      throw error;
    }
  }
);

// Get DEX Schedule
export const getDexSchedule = createAsyncThunk(
  "admin/getDexSchedule",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}/dex-schedule`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching DEX schedule");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching DEX schedule:', error);
      throw error;
    }
  }
);

// EMPLOYER CUSTOM PRICING

// Get employer custom pricing
export const getEmployerCustomPricing = createAsyncThunk(
  "admin/getEmployerCustomPricing",
  async ({ employerId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/employers/${employerId}/custom-pricing`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching employer custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching employer custom pricing:', error);
      throw error;
    }
  }
);

// Update employer custom pricing
export const updateEmployerCustomPricing = createAsyncThunk(
  "admin/updateEmployerCustomPricing",
  async ({ employerId, customPerEmployeeAmount, customPerFamilyEmployeeAmount }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/employers/${employerId}/custom-pricing`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({
            customPerEmployeeAmount,
            customPerFamilyEmployeeAmount,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Error updating employer custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating employer custom pricing:', error);
      throw error;
    }
  }
);

// Remove employer custom pricing
export const removeEmployerCustomPricing = createAsyncThunk(
  "admin/removeEmployerCustomPricing",
  async ({ employerId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/employers/${employerId}/custom-pricing`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error removing employer custom pricing");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing employer custom pricing:', error);
      throw error;
    }
  }
);

// Get users with failed payments
export const getFailedPayments = createAsyncThunk(
  "admin/getFailedPayments",
  async ({ step, page = 1, limit = 50 } = {}) => {
    try {
      const token = await getFirebaseAuthToken();
      const params = new URLSearchParams();
      if (step) params.append('step', step);
      params.append('page', page);
      params.append('limit', limit);

      const response = await fetch(
        `${API_BASE}/dashboard/admin/failed-payments?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error fetching failed payments");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching failed payments:', error);
      throw error;
    }
  }
);

// Send failed payment notification email to user
export const sendFailedPaymentNotification = createAsyncThunk(
  "admin/sendFailedPaymentNotification",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/schedule-monitoring/failed-payments/${userId}/notify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error sending notification");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending failed payment notification:', error);
      throw error;
    }
  }
);

// Process failed payments cronjob manually
export const processFailedPayments = createAsyncThunk(
  "admin/processFailedPayments",
  async () => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/failed-payments/process`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error processing failed payments");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error processing failed payments:', error);
      throw error;
    }
  }
);

// Retry payment for a specific user
export const retryFailedPayment = createAsyncThunk(
  "admin/retryFailedPayment",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/failed-payments/${userId}/retry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error retrying payment");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error retrying payment:', error);
      throw error;
    }
  }
);

// Clear failed payment status for a user
export const clearFailedPaymentStatus = createAsyncThunk(
  "admin/clearFailedPaymentStatus",
  async ({ userId, reason }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/failed-payments/${userId}/clear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ reason }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error clearing failed payment status");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error clearing failed payment status:', error);
      throw error;
    }
  }
);

// Delete App User (from Member Management)
export const deleteAppUser = createAsyncThunk(
  "admin/deleteAppUser",
  async ({ userId }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/app-users/${userId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error deleting app user");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting app user:', error);
      throw error;
    }
  }
);

// ===== PROMO CODES =====

// Get all promo codes
export const getPromoCodes = createAsyncThunk(
  "admin/getPromoCodes",
  async ({ page = 0, limit = 10, active, search } = {}) => {
    try {
      const token = await getFirebaseAuthToken();
      const params = new URLSearchParams({ page, limit });
      if (active) params.append("active", active);
      if (search) params.append("search", search);

      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching promo codes");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      throw error;
    }
  }
);

// Get single promo code
export const getPromoCode = createAsyncThunk(
  "admin/getPromoCode",
  async ({ id }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching promo code");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo code:', error);
      throw error;
    }
  }
);

// Create promo code
export const createPromoCode = createAsyncThunk(
  "admin/createPromoCode",
  async (body) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error creating promo code");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating promo code:', error);
      throw error;
    }
  }
);

// Update promo code
export const updatePromoCode = createAsyncThunk(
  "admin/updatePromoCode",
  async ({ id, ...body }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error updating promo code");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating promo code:', error);
      throw error;
    }
  }
);

// Toggle promo code active/inactive
export const togglePromoCode = createAsyncThunk(
  "admin/togglePromoCode",
  async ({ id, active }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes/${id}/toggle`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify({ active }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error toggling promo code");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling promo code:', error);
      throw error;
    }
  }
);

// Get promo code redemptions
export const getPromoCodeRedemptions = createAsyncThunk(
  "admin/getPromoCodeRedemptions",
  async ({ id, page = 0, limit = 20 }) => {
    try {
      const token = await getFirebaseAuthToken();
      const response = await fetch(
        `${API_BASE}/dashboard/admin/promo-codes/${id}/redemptions?page=${page}&limit=${limit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching redemptions");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching promo code redemptions:', error);
      throw error;
    }
  }
);
